import { log } from 'three/tsl';
import buildingsConfig from '../../../storyline/buildings.json'
export const BLACK_LISTED_BUILDINGS = ['debris_build', 'spaceship', 'mine', 'debris']

export const KEY_CODES = {
  LEFT_ARROW: 37,
  UP_ARROW: 38,
  RIGHT_ARROW: 39,
  DOWN_ARROW: 40,
  SHIFT: 16,
  Q: 81,
  E: 69,
  G: 71,
  A: 65,
  W: 87,
  S: 83,
  D: 68,
  P: 80,
  O: 79,
}

export function getDeepValue(source, field) {
  let f = field.replace(/\//g, '.'); // replace '/' to '.'
  f = f.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  f = f.replace(/^\./, ''); // strip a leading dot
  const path = f.split('.');
  for (let i = 0, n = path.length; i < n; ++i) {
    const partName = path[i];
    if (source && partName in source) {
      source = source[partName];
    } else {
      return;
    }
  }
  return source;
}

export function sortBy(arr, field, direction = 'asc') {
  const dir = direction === 'desc' ? -1 : 1
  return [...arr].sort((a, b) => {
    const av = getDeepValue(a, field)
    const bv = getDeepValue(b, field)
    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1
    if (typeof av === 'string' || typeof bv === 'string') {
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' })
      return dir * cmp
    }
    return dir * (av - bv)
  })
}

export function canAttack(building, userMap, visitorMap, visitorInfo, attacking) {
  if (!visitorMap.mapId || !attacking) {
    return false
  }

  const searching_param = 'level'
  const userMax = getMaxAmountOfList(userMap.buildings, searching_param)
  const buildingAttack = checkBuilding(userMax, building)

  const isAttacked = checkAttackedBuilding(visitorInfo.playersAttacking, building)
  const countedBattles = visitorInfo.playersAttacking
    ? Object.keys(visitorInfo.playersAttacking).length
    : 0
  return isAttacked || countedBattles >= 2 ? false : buildingAttack
}

export function checkBuildingState(building) {
  return building.built && !building.evolving && !building.destroying && !building.blocked
}

export function playerCanBeAttacked(userBuildings, player) {
  const userHasBuildings = Object.keys(userBuildings).length > 2 // ignore mine and spaceship
  const playerHasSettlers = player.settlers && player.settlers > 0
  return userHasBuildings && playerHasSettlers
}

export function getNeightborsPos(tile) {
  return [
    { q: tile.q, r: tile.r + 1 }, //NE
    { q: tile.q + 1, r: tile.r }, //E
    { q: tile.q + 1, r: tile.r - 1 }, //SE
    { q: tile.q, r: tile.r - 1 }, //SW
    { q: tile.q - 1, r: tile.r }, //W
    { q: tile.q - 1, r: tile.r + 1 }, //NW
  ]
}

/**
 * In order to avoid a change turn process Building model has changed to store number of turn where building operation are effective
 * instead of booleans that need to be changed, this function adapt new model to old one
 * @param {Object} building
 * @param {Object} mapBuildings
 * @param {number} currentTurn
 * @returns
 */
export function parseBuilding(building, mapBuildings, currentTurn) {
  // return ajacent same type buildings for a given building
  const _getAdjacentBuildings = (b) => {
    const adjacentKeys = [
      `${b.q}_${b.r + 1}`,
      `${b.q + 1}_${b.r}`,
      `${b.q + 1}_${b.r - 1}`,
      `${b.q}_${b.r - 1}`,
      `${b.q - 1}_${b.r}`,
      `${b.q - 1}_${b.r + 1}`,
    ]
    return adjacentKeys.filter(
      (neighbour) => mapBuildings[neighbour] && mapBuildings[neighbour].type === b.type
    )
  }

  const config = building.name ? buildingsConfig[building.name] : null
  if (config) {
    // destroyed building
    if (building.destroying && building.destroying <= currentTurn) {
      return null
    }
    // built now stores the turn where building is built
    building.built = building.built === true || building.built <= currentTurn
    // blocked now stores the turn where building is blocked (attacked), boolean false if not
    building.blocked = Number.isInteger(building.blocked)
      ? building.blocked <= currentTurn
      : building.blocked === true
    // building is evolving
    building.evolving =
      building.evolving === false || building.evolving > currentTurn ? building.evolving : false
    // building now stores evolution as an array of turns where each evolution is done
    building.level =
      building.evolution && building.evolution.length
        ? building.evolution.filter((t) => t <= currentTurn).length + 1
        : building.level
    building.level = Math.min(config.maxLevel, building.level)
    // production multiplier duplicates on same type buildings chain bigger than 1 this is when
    // building has more than 1 adjacent building or its unique adjacent building has more than one
    let adjacents = _getAdjacentBuildings(building)
    if (adjacents.length === 1) {
      adjacents = _getAdjacentBuildings(mapBuildings[adjacents[0]])
    }
    building.multiplier = adjacents.length > 1 ? 2 : 1
    // production is 0 for not built or destroying buildings
    building.production =
      building.built && !building.destroying
        ? config.productionAmount[building.level - 1] * building.multiplier
        : 0
  }
  return building
}

export function countBuildingsAttr(attr, value, buildings) {
  return buildings.filter((building) => building[attr] == value).length
}

export function getBuildingStatus(building) {
  let status = null

  if (!building.built) {
    status = 'construction'
  }
  if (building.built && building.evolving != false) {
    status = 'evolving'
  }
  if (building.built && building.destroying != false) {
    status = 'destroying'
  }
  if (building.built && building.blocked != false) {
    status = 'blocked'
  }

  return status
}

export function getAttrsByType(obj, type) {
  let result = {}
  Object.keys(obj).map((key) => {
    if (typeof obj[key] == type) result[key] = obj[key]
  })
  return result
}

export function getBuildingIcon(building) {
  let icon = ''
  if (building.built) {
    if (building.evolving) {
      icon = 'upgrade'
    } else if (building.destroying) {
      icon = 'ball'
    } else if (building.blocked) {
      icon = 'desafio'
    } else if (building.type == 'debris') {
      icon = 'empty'
    } else {
      icon = building.type
    }
  } else {
    icon = 'crane'
  }

  return icon
}

export function getNextLevelBuilding(building, building_config) {
  if (!building || !building_config) {
    return null
  }
  let copy = Object.assign({}, building)
  let maxLevel = building_config.maxLevel

  if (copy.level) {
    let level = copy.level + 1 < maxLevel ? copy.level + 1 : maxLevel
    let arrayIndex = level - 1
    let multiplier = copy.multiplier
    let production_base =
      building_config.productionAmount[
        level >= maxLevel ? building_config.productionAmount.length - 1 : arrayIndex
      ]
    copy.level = level
    copy.production = multiplier == 1 ? production_base : production_base * multiplier
  }

  return copy
}

export function isBlackListed(name) {
  return BLACK_LISTED_BUILDINGS.includes(name)
}

export function getMaxAmountBetweenList(list1, list2) {
  const filteredList1 = Object.values(list1)
  const filteredList2 = Object.values(list2)
  let result = {}
  let maxValue = 0

  if (filteredList1.length < 2 && filteredList2.length < 2) return result

  filteredList1.forEach((elementA) => {
    filteredList2.forEach((elementB) => {
      if (elementA['level'] && elementB['level']) {
        if (elementA['level'] >= elementB['level']) {
          maxValue = elementA['level']
        }
        if (result['level'] <= maxValue || !result['level']) {
          result['level'] = maxValue
        }
      }
    })
  })
  return result
}
// export function checkAttackColony(userList, visitorList){
//   const newUserList = Object.values(userList)
//   const newVisitorList = Object.values(visitorList)
//   let result = false
//   let maxLevel = 0
//   if (newUserList.length == 0) {
//     return result
//   }
//
//   new
//
//
// }

export function checkMissing(objectA, objectB) {
  return Object.keys(objectB).filter((key) => !objectA[key])
}

export function getMaxAmountOfList(list, maxAmountParameter) {
  let filteredList = Object.values(list)
  let max = 0
  filteredList.forEach((element) => {
    if (!BLACK_LISTED_BUILDINGS.includes(element.name) && checkBuildingState(element)) {
      max = element[maxAmountParameter] > max ? element[maxAmountParameter] : max
    }
  })

  return max
}
export function checkBuilding(userLevel, building) {
  if (
    building.evolving ||
    !building.built ||
    userLevel < building.level ||
    building.name == 'mine' ||
    building.name == 'spaceship' ||
    building.name == 'debris_build'
  ) {
    return false
  }
  return true
}
export function checkAttackedBuilding(playersAttacking, building) {
  if (!playersAttacking) {
    return false
  }
  let attacked = false

  Object.keys(playersAttacking).map((id) => {
    if (building.q == playersAttacking[id].q && building.r == playersAttacking[id].r) {
      attacked = true
    }
  })

  return attacked
}
export function getModifiedAttributes(newObject, oldObject) {
  let result = {}
  Object.keys(newObject).map((object) => {
    if (!oldObject[object] || newObject[object] != oldObject[object]) {
      result[object] = newObject[object]
    }
  })

  return result
}

export function compareBuildingState(oldBuilding, newBuilding) {
  let changed = false

  if (oldBuilding.evolving != newBuilding.evolving) {
    changed = true
  }
  if (oldBuilding.destroying != newBuilding.destroying) {
    changed = true
  }

  if (oldBuilding.blocked != newBuilding.blocked) {
    changed = true
  }

  return changed
}

export function getDiffState(oldObject, newObject) {
  let result = {
    creations: {},
    updates: {},
    deletions: {},
    rockRemoving: {},
  }
  const newBuildings = newObject.buildings
  const oldBuildings = oldObject.buildings
  Object.keys(newBuildings).map((object) => {
    if (!oldBuildings[object]) {
      result.creations[object] = newBuildings[object]
    }
    if (
      oldBuildings[object] &&
      newBuildings[object] != oldBuildings[object] &&
      compareBuildingState(oldBuildings[object], newBuildings[object])
    ) {
      result.updates[object] = newBuildings[object]
    }
  })
  // result.deletions = getModifiedAttributes(newBuildings, oldBuildings)
  // result.rockRemoving = getModifiedAttributes(oldObject.rockRemoving, newObject.rockRemoving)

  return result
}

export function removeFromArray(elem, array) {
  let pos = array.indexOf(elem)

  if (pos > -1) {
    array.splice(pos, 1)
    return true
  } else {
    return false
  }
}

export function replaceInArray(elem, replace, array) {
  let elemPos = array.findIndex((found) => found.id == elem.id)

  if (elemPos > -1 && elem != replace) {
    array.splice(elemPos, 1, replace)
    return true
  } else {
    return false
  }
}

export function getSameNeightbors(search, array) {
  let tiles = array.filter((tile) => tile.type == search.type)
  let neightbours = []
  let evaluatedNeightbours = []
  let neightIndex = 0

  do {
    if (search) {
      let directions = getNeightborsPos(search)

      for (let direction of directions) {
        let found = tiles.find((tile) => tile.q == direction.q && tile.r == direction.r)

        if (found && !neightbours.includes(found)) {
          neightbours.push(found)
        }
      }

      if (!evaluatedNeightbours.includes(search)) {
        evaluatedNeightbours.push(search)
      }
      if (!neightbours.includes(search)) {
        neightbours.push(search)
      }

      search = neightbours[neightIndex]
      removeFromArray(search, tiles)
      neightIndex++
    }
  } while (neightbours.length != evaluatedNeightbours.length)

  return neightbours
}

export function paramString(name, defaultValue) {
  const queryMatch = document.location.href.match(new RegExp(name + '=([^&]+)'))
  if (queryMatch) {
    return queryMatch[1]
  } else {
    return defaultValue
  }
}

export function localAsset(relativePath) {
  return '../../../assets/' + relativePath
}

export function asset(relativePath) {
  return process.env.ASSETS_HOST + '/' + relativePath
}

export function numbersDiff(numbA, numbB) {
  let diff = 0
  let start = numbA < numbB ? numbA : numbB
  let finish = numbA > numbB ? numbA : numbB

  while (start != finish) {
    diff++
    start++
  }

  return diff
}

export function getRandomSeedId(min, max) {
  let randomNumb = Math.floor(Math.random() * (max - min) + min)
  return 'seed_' + randomNumb
}
export function rollDices(numberDices, faces) {
  let out = []
  for (let i = 0; i < numberDices; i++) {
    out.push(Math.floor(Math.random() * faces) + 1)
  }
  return out
}
export function getRandomTipId() {
  let out = 'loader.tips.'
  let id = rollDices(1, 50)[0]
  out += id
  return out
}

export function paramInt(name, defaultValue) {
  return parseInt(paramString(name, defaultValue + ''))
}
export function paramFloat(name, defaultValue) {
  return parseFloat(paramString(name, defaultValue + ''))
}
export function varying(...values) {
  return values[Math.round(Math.random() * (values.length - 1))]
}
